import { useEffect, useState, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Download, FileType, Star, MessageSquare, RotateCw, Send, Loader2, Upload, X, Image as ImageIcon, Compass, Link2, CheckCircle2, BookOpen, ArrowRight, Layers, Code2 } from "lucide-react";
import { OutputBlock } from "@/components/Output";
import { toast } from "sonner";
import { useSubscription } from "@/contexts/SubscriptionContext";
import UpgradeOverlay from "@/components/UpgradeOverlay";
import GenerateLoader from "@/components/GenerateLoader";
import { TOOL_META } from "@/lib/toolMeta";
import { meta } from "@/lib/metaPixel";

// Per-tool "How to publish" resource slugs. Maps each tool key to its guide.
const HOW_TO_PUBLISH = {
  reel: "how-to-post-reels",
  one_dive_five_reels: "how-to-post-reels",
  emulation: "how-to-use-emulation-mode",
  hook_bank: "how-to-post-reels",
  viral_carousel: "how-to-post-carousels",
  caption_rewrite: "how-to-post-reels",
  content_plan: "how-to-execute-content-plan",
  email_newsletter: "how-to-send-emails",
  email_campaign: "how-to-send-emails",
  meta_ads: "how-to-launch-meta-ads",
  google_ads: "how-to-launch-google-ads",
  seo_blog: "how-to-publish-seo-blogs",
  seo_product_page: "how-to-publish-seo-blogs",
  review_response: "how-to-reply-to-reviews",
  course_promo: "how-to-post-reels",
  gear_promo: "how-to-post-reels",
  sales_funnel: "how-to-execute-campaigns",
  brand_deal: "how-to-send-emails",
};

// Tools whose output can be handed into the Branded HTML Studio. Value = button label.
const HTML_STUDIO_TOOLS = {
  newsletter: "Build branded email",
  email_campaign: "Build branded email",
  funnel: "Build sales page",
  course: "Build landing page",
  gear: "Build product page",
  seo_product_page: "Build web page",
};

function ToolDescription({ toolKey, tier, badgeForTier, credits }) {
  const meta = TOOL_META[toolKey];
  if (!meta) return null;
  const badgeLabel = tier === "pro" ? "Business" : "Creator";
  return (
    <div data-testid="tool-description" className="mt-6 dms-card p-5 sm:p-6 border-l-4 border-l-[#4FD1C5]">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#1D4E89]">About this tool</span>
        <span className="text-[10.5px] tracking-[0.18em] uppercase font-bold px-2 py-0.5 rounded-full bg-[#0A2342] text-[#4FD1C5]" data-testid="tool-tier-badge">{badgeLabel}</span>
        {credits != null && (
          <span data-testid="tool-credit-cost" className="text-[10.5px] tracking-[0.18em] uppercase font-bold px-2 py-0.5 rounded-full bg-[#4FD1C5]/20 text-[#0F766E]">
            {credits} credit{credits === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <p className="text-[14.5px] text-[#1F2937] leading-relaxed">{meta.description}</p>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B] mb-2">What you&apos;ll get</div>
          <ul className="list-disc pl-5 space-y-1 text-[13.5px] text-[#1F2937]">
            {meta.whatYouGet.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B] mb-2">How to use this</div>
          <p className="text-[13.5px] text-[#1F2937] leading-relaxed">{meta.howToUse}</p>
        </div>
      </div>
    </div>
  );
}

function RefinePanel({ vaultId, onRefined }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const { refresh: refreshSub } = useSubscription();

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!prompt.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/tools/refine", { vault_id: vaultId, refinement: prompt.trim() });
      onRefined?.(data);
      setPrompt("");
      toast.success("Updated output saved to your Vault");
      refreshSub?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) {
        toast.error("You are out of queries. Top up from Billing.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Refine failed. Try again.");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="dms-card p-4 sm:p-5" data-testid="refine-panel">
      {!open ? (
        <button data-testid="refine-open" onClick={() => setOpen(true)} className="w-full inline-flex items-center justify-center gap-2 text-[14px] font-semibold text-[#0A2342] hover:text-[#1D4E89] py-2">
          <MessageSquare className="w-4 h-4" /> Request changes or retry
        </button>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div className="flex items-center gap-2 text-[13px] text-[#1F2937]">
            <RotateCw className="w-4 h-4 text-[#1D4E89]" />
            <span className="font-heading font-bold">Tell me what to change.</span>
          </div>
          <textarea
            data-testid="refine-input" value={prompt} onChange={(e) => setPrompt(e.target.value)}
            rows={3} placeholder="e.g. Make the caption punchier and cut the intro by half. Change the CTA to a DM prompt."
            className="dms-textarea" required
          />
          <div className="text-[11.5px] text-[#94A3B8]">This uses 1 query. The updated output is saved as a new item in your Vault, linked to the original.</div>
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => { setOpen(false); setPrompt(""); }} className="dms-btn-ghost">Cancel</button>
            <button type="submit" disabled={busy} data-testid="refine-submit" className="dms-btn-primary">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Refining</> : <><Send className="w-4 h-4" /> Send refinement</>}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function ToolShell({ testid, eyebrow, title, subtitle, fields, endpoint, transformPayload, renderOutput, tier = "basic", toolKey }) {
  const [state, setState] = useState({});
  const [output, setOutput] = useState(null);
  const [vaultId, setVaultId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [banked, setBanked] = useState(false);
  const [toolConf, setToolConf] = useState(null);
  const [linkedTask, setLinkedTask] = useState(null); // Sprint 4: today's-task pre-fill
  const [linkedTaskDone, setLinkedTaskDone] = useState(false);
  const { canUse, sub, refresh: refreshSub, badgeForTier } = useSubscription();
  const location = useLocation();

  // Sprint 4: When the user arrives via /tools/reel?task=<id>, fetch the task,
  // prefill sensible fields, and remember the task_id so we can post it back
  // and mark the task done on submit.
  const taskIdFromUrl = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get("task") || "";
  }, [location.search]);

  // Workspace named in the daily-email deep-link. For executors who manage
  // several brands we must switch into this workspace BEFORE loading the task,
  // so the task resolves AND the tool generates against the correct brand.
  const workspaceFromUrl = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get("workspace") || "";
  }, [location.search]);

  useEffect(() => {
    if (!taskIdFromUrl) return;
    let cancelled = false;
    (async () => {
      if (workspaceFromUrl) {
        try {
          await api.post(`/workspaces/${workspaceFromUrl}/activate`);
        } catch (e) {
          console.warn("Could not switch to the email's workspace", e);
        }
      }
      try {
        const { data } = await api.get(`/strategy/calendar/task/${taskIdFromUrl}`);
        if (cancelled) return;
        setLinkedTask(data);
        setLinkedTaskDone(data.status === "done");
        // Prefill the common fields: topic (hook), hook_idea (hook), angle → footage/description context.
        const prefillHook = data.hook || "";
        const prefillAngle = data.angle || "";
        const combinedTopic = prefillHook
          ? (prefillAngle ? `${prefillHook}\n\n(Angle: ${prefillAngle})` : prefillHook)
          : prefillAngle;
        setState((s) => ({
          ...s,
          ...(s.topic ? {} : (combinedTopic ? { topic: combinedTopic } : {})),
          ...(s.hook_idea ? {} : (prefillHook ? { hook_idea: prefillHook } : {})),
        }));
      } catch (e) {
        if (!cancelled) console.warn("Could not load the linked task for prefill", e);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskIdFromUrl, workspaceFromUrl]);

  useEffect(() => {
    api.get("/tools/config").then(({ data }) => {
      const backendKey = endpoint.replace(/^\/tools\//, "").replace(/-/g, "_");
      const aliases = {
        "content_plan": "content_plan", "hook_bank": "hook_bank", "caption_rewrite": "caption_rewrite",
        "viral_carousel": "viral_carousel", "email_newsletter": "email_newsletter", "email_campaign": "email_campaign",
        "meta_ads": "meta_ads", "google_ads": "google_ads", "seo_blog": "seo_blog", "review_response": "review_response",
        "course_promo": "course_promo", "gear_promo": "gear_promo", "sales_funnel": "sales_funnel",
        "brand_deal": "brand_deal", "one_dive": "one_dive_five_reels", "emulation": "emulation", "reel": "reel",
      };
      const resolved = aliases[backendKey] || backendKey;
      const found = (data.items || []).find((x) => x.tool === resolved);
      setToolConf(found || null);
    }).catch(() => {});
  }, [endpoint, sub?.status, sub?.tier]);

  const locked = toolConf ? !toolConf.allowed : !canUse(tier);
  const trialGated = toolConf?.reason === "trial_gated";
  const outOfCredits = toolConf?.reason === "out_of_credits" || (sub && !sub.is_unlimited_credits && sub.credits_remaining != null && toolConf && sub.credits_remaining < (toolConf.credit_cost || 1));
  const creditCost = toolConf?.credit_cost;

  const handleChange = (key, value) => setState((s) => ({ ...s, [key]: value }));

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    setOutput(null);
    setVaultId(null);
    setBanked(false);
    try {
      const payloadBase = transformPayload ? transformPayload(state) : state;
      const payload = taskIdFromUrl ? { ...payloadBase, task_id: taskIdFromUrl } : payloadBase;
      const { data } = await api.post(endpoint, payload);
      setOutput(data.output);
      setVaultId(data.id);
      // Meta: DMS_ContentGenerated fires per successful generation (this is a
      // repeatable engagement event, not a one-shot conversion — so we do NOT
      // dedupe). Only controlled category values are sent — never the caption,
      // hook, angle or generated content itself.
      meta.contentGenerated(toolKey || "unknown", linkedTask?.channel || undefined);
      if (data.linked_task) {
        setLinkedTaskDone(true);
        toast.success("Saved to Vault + today's task marked done");
      } else {
        toast.success("Saved to Content Vault");
      }
      refreshSub?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 402) {
        if (typeof detail === "object") {
          if (detail.error === "trial_gated") toast.error(detail.message || "Upgrade to unlock this tool");
          else if (detail.error === "out_of_credits") toast.error(detail.message || "You are out of Strategy Credits");
          else if (detail.error === "wrong_tier") toast.error("This tool needs a higher plan.");
          else toast.error("Upgrade required for this tool.");
        } else {
          toast.error(typeof detail === "string" ? detail : "Upgrade required.");
        }
      } else {
        toast.error(typeof detail === "string" ? detail : "Generation failed. Try again.");
      }
    } finally { setBusy(false); }
  };

  const toggleBank = async () => {
    if (!vaultId) return;
    try {
      await api.put(`/vault/${vaultId}`, { is_banked: !banked });
      setBanked(!banked);
      toast.success(banked ? "Removed from bank" : "Banked for later");
    } catch { toast.error("Could not update"); }
  };

  const onRefined = (data) => {
    setOutput(data.output);
    setVaultId(data.id);
    setBanked(false);
  };

  return (
    <div data-testid={testid} className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-5xl relative">
      <div className="dms-label mb-3">{eyebrow}</div>
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342]">{title}</h1>
      <p className="mt-2 text-[#475569] max-w-2xl text-[14.5px]">{subtitle}</p>

      {locked && !trialGated && (
        <UpgradeOverlay requiredTier={tier} title={`Unlock ${title}`} body={`${title} is part of the ${tier === "pro" ? "Pro" : "Creator"} plan.`} />
      )}
      {trialGated && (
        <div data-testid="tool-trial-gated" className="mt-6 dms-card p-5 sm:p-6 border-[#FCD34D] bg-[#FEFCE8]">
          <div className="dms-label text-[#92400E]">Advanced tool</div>
          <h2 className="mt-1.5 font-heading font-bold text-[#0A2342] text-xl">Available on paid plans</h2>
          <p className="mt-2 text-[14.5px] text-[#1F2937]">
            This is an advanced tool available on paid plans. Upgrade to unlock this campaign builder.
          </p>
          <a href="/billing" data-testid="tool-upgrade-cta" className="mt-4 inline-flex dms-btn-accent">Upgrade To Unlock</a>
        </div>
      )}
      {outOfCredits && !locked && (
        <div className="mt-6 dms-card p-4 sm:p-5 border-[#FCD34D] bg-[#FEFCE8] text-[#1F2937] text-[14px]">
          You don&apos;t have enough Strategy Credits for this tool (needs {creditCost || "?"}). <a href="/billing" className="font-semibold text-[#1D4E89] hover:text-[#0A2342] underline">Upgrade -&gt;</a>
        </div>
      )}

      {toolKey && <ToolDescription toolKey={toolKey} tier={tier} badgeForTier={badgeForTier} credits={creditCost} />}

      {toolKey && HOW_TO_PUBLISH[toolKey] && (
        <div className="mt-4 flex items-center gap-3 rounded-md border border-[#E2E8F0] bg-white px-4 py-3" data-testid="how-to-publish-callout">
          <div className="w-8 h-8 rounded-md bg-[#0A2342]/6 text-[#0A2342] flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-4 h-4" />
          </div>
          <div className="flex-1 text-[13px] text-[#475569] leading-snug">
            Once you generate this output, follow the publishing steps so it lands correctly on the right platform.
          </div>
          <Link
            to={`/downloads/${HOW_TO_PUBLISH[toolKey]}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`how-to-publish-link-${toolKey}`}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1D4E89] hover:text-[#0A2342] whitespace-nowrap"
          >
            How to publish <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {linkedTask && (
        <div
          data-testid="tool-linked-task-banner"
          className={`mt-6 dms-card p-4 sm:p-5 border-l-4 flex flex-wrap items-start gap-3 ${
            linkedTaskDone ? "border-l-emerald-500 bg-emerald-50/40" : "border-l-[#4FD1C5] bg-[#F0FDFA]/40"
          }`}
        >
          <div className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${
            linkedTaskDone ? "bg-emerald-100 text-emerald-700" : "bg-[#0A2342] text-[#4FD1C5]"
          }`}>
            {linkedTaskDone ? <CheckCircle2 className="w-5 h-5" /> : <Compass className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-[#1D4E89] flex items-center gap-1.5">
              <Link2 className="w-3 h-3" /> Today&apos;s calendar task · Day {linkedTask.day_number} · Week {linkedTask.week_number}
              {linkedTaskDone && <span className="ml-1 text-emerald-700">· Done</span>}
            </div>
            <div className="mt-1 text-[15px] font-heading font-bold text-[#0A2342] leading-snug">{linkedTask.hook}</div>
            {linkedTask.cta && <div className="mt-1 text-[12.5px] text-[#334155]"><b>CTA:</b> {linkedTask.cta}</div>}
            <div className="mt-1 text-[11.5px] text-[#64748B]">
              {linkedTaskDone
                ? "This task is already marked done — running the tool again won't change that."
                : "Fields below are pre-filled. Generating will save the output to your Vault and mark this task done."}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="mt-6 sm:mt-8 dms-card p-5 sm:p-6 space-y-4 sm:space-y-5">
        {fields.map((f) => (
          <div key={f.key}>
            <label className="dms-label block mb-2">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea data-testid={`field-${f.key}`} value={state[f.key] || ""} onChange={(e) => handleChange(f.key, e.target.value)} rows={f.rows || 4} placeholder={f.placeholder} required={f.required} className="dms-textarea" />
            ) : f.type === "number" ? (
              <input data-testid={`field-${f.key}`} type="number" min={f.min} max={f.max} value={state[f.key] ?? f.defaultValue ?? ""} onChange={(e) => handleChange(f.key, Number(e.target.value))} className="dms-input w-32" />
            ) : f.type === "file" ? (
              <FileField testid={`field-${f.key}`} value={state[f.key]} onChange={(v) => handleChange(f.key, v)} accept={f.accept || "image/*"} />
            ) : (
              <input data-testid={`field-${f.key}`} value={state[f.key] || ""} onChange={(e) => handleChange(f.key, e.target.value)} placeholder={f.placeholder} required={f.required} className="dms-input" />
            )}
            {f.help && <p className="mt-1.5 text-xs text-[#64748B]">{f.help}</p>}
          </div>
        ))}
        <div className="flex justify-end">
          <button data-testid="tool-submit" type="submit" disabled={busy || outOfCredits || trialGated} className="dms-btn-primary">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating</> : (creditCost ? `Generate (${creditCost} credit${creditCost === 1 ? "" : "s"})` : "Generate")}
          </button>
        </div>
      </form>

      {busy && (
        <div className="mt-6">
          <GenerateLoader label={`Building your ${title.toLowerCase()}`} />
        </div>
      )}

      {output && (
        <div data-testid="tool-output" className="mt-8 sm:mt-10 space-y-4 sm:space-y-5 animate-fade-up">
          {vaultId && (
            <div className="dms-card p-3 sm:p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-[13px] text-[#1F2937]">
                <span className="dms-label inline">Saved</span> &nbsp; This output is in your <a href="/vault" className="font-semibold text-[#1D4E89] hover:text-[#0A2342] underline">Content Vault</a>.
              </div>
              <div className="flex flex-wrap gap-2">
                <a href={`${API}/vault/${vaultId}/download/txt`} data-testid="tool-download-txt" className="dms-btn-ghost text-[13px] py-2 px-3"><Download className="w-3.5 h-3.5" /> TXT</a>
                <a href={`${API}/vault/${vaultId}/download/docx`} data-testid="tool-download-docx" className="dms-btn-ghost text-[13px] py-2 px-3"><FileType className="w-3.5 h-3.5" /> DOCX</a>
                {toolKey === "carousel" && (
                  <Link
                    to={`/tools/viral-carousel/slides/${vaultId}`}
                    data-testid="tool-create-slides"
                    className="text-[13px] inline-flex items-center gap-1.5 py-2 px-3 rounded-md bg-[#0A2342] text-white hover:bg-[#1D4E89] transition font-semibold"
                  >
                    <Layers className="w-3.5 h-3.5" /> Create slides
                  </Link>
                )}
                {HTML_STUDIO_TOOLS[toolKey] && (
                  <Link
                    to={`/tools/html-studio?from=${vaultId}`}
                    data-testid="tool-build-html"
                    className="text-[13px] inline-flex items-center gap-1.5 py-2 px-3 rounded-md bg-[#0A2342] text-white hover:bg-[#1D4E89] transition font-semibold"
                  >
                    <Code2 className="w-3.5 h-3.5" /> {HTML_STUDIO_TOOLS[toolKey]}
                  </Link>
                )}
                <button onClick={toggleBank} data-testid="tool-bank" className={`text-[13px] inline-flex items-center gap-1.5 py-2 px-3 rounded-md border ${banked ? "border-[#F59E0B] text-[#F59E0B] bg-[#FFFBEB]" : "border-[#E2E8F0] text-[#1F2937] bg-white hover:border-[#F59E0B]"}`}>
                  <Star className="w-3.5 h-3.5" fill={banked ? "currentColor" : "none"} /> {banked ? "Banked" : "Bank for later"}
                </button>
              </div>
            </div>
          )}
          {renderOutput(output)}
          {vaultId && <RefinePanel vaultId={vaultId} onRefined={onRefined} />}
        </div>
      )}
    </div>
  );
}

export function HookRow({ hook, archetype, trigger }) {
  return (
    <div className="flex flex-wrap items-start gap-3 py-2.5 border-b border-[#E2E8F0] last:border-0">
      <div className="flex-1 min-w-[180px] text-[14.5px] sm:text-[15px] text-[#1F2937]">&ldquo;{hook}&rdquo;</div>
      {archetype && <span className="dms-badge">{archetype}</span>}
      {trigger && <span className="text-[11px] tracking-[0.18em] uppercase font-bold text-[#64748B]">{trigger}</span>}
    </div>
  );
}

export { OutputBlock };

function FileField({ testid, value, onChange, accept }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ file_id: data.file_id, url: data.url, name: file.name });
      toast.success("Uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div>
      {value?.file_id ? (
        <div className="flex items-center gap-3 dms-card p-3">
          <ImageIcon className="w-4 h-4 text-[#1D4E89]" />
          <div className="flex-1 min-w-0 text-[13.5px] text-[#1F2937] truncate">{value.name || value.url}</div>
          <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange(null)} className="text-[#B91C1C] hover:text-[#7F1D1D]"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <label htmlFor={testid} className="block cursor-pointer rounded-md border-2 border-dashed border-[#E2E8F0] bg-white p-5 text-center hover:border-[#4FD1C5] transition">
          <Upload className="w-5 h-5 mx-auto text-[#1D4E89] mb-2" />
          <div className="text-[13.5px] font-semibold text-[#0A2342]">{busy ? "Uploading…" : "Click to upload"}</div>
          <div className="text-[11.5px] text-[#64748B] mt-1">{accept}</div>
          <input id={testid} data-testid={testid} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
      )}
    </div>
  );
}
